import GL from "_src/engine/gl";
import {pixelBufferType, RBIntFormat} from "_src/engine/constants";
import resourceManager from "_src/render_graph/resource_manager";
import glTypes from "_src/engine/gl_types";

let _cache = {};
let _webgl1Dictionary = {};
let _webgl2Dictionary = {};

let _maxSamples;
let _multisamplingSupported;

function webgl1Get(name) {

  const gl = GL.getContext();

  switch(name) {
    case "OES_vertex_array_object":
      return getVertexArrayObject(gl);
    case "WEBGL_draw_buffers":
      return getMultipleRenderTargets(gl);
    case "ANGLE_instanced_arrays":
      return getInstancedArrays(gl);
    case "EXT_texture_filter_anisotropic":
      return getTextureFilterAnisotropic(gl);
    case "EXT_blend_minmax":
      return getMinMaxBlending(gl);
    default:
      if (name in _webgl1Dictionary) {
        const originalName = _webgl1Dictionary[name];
        return gl.getExtension(originalName);
      } else {
        return gl.getExtension(name);
      }
  }

}

function webgl2Get(name) {

  const gl = GL.getContext();

  switch(name) {
    case "OES_vertex_array_object":
    case "OES_element_index_uint":
    case "OES_standard_derivatives":
    case "WEBGL_draw_buffers":
    case "ANGLE_instanced_arrays":
    case "EXT_blend_minmax":
    case "EXT_frag_depth":
      return gl;
    case "EXT_texture_filter_anisotropic":
      return getTextureFilterAnisotropic(gl);
    default:
      if (name in _webgl2Dictionary) {
        const originalName = _webgl2Dictionary[name];
        return gl.getExtension(originalName);
      } else {
        return gl.getExtension(name);
      }
  }

}

function getVertexArrayObject(gl) {

  const ext = gl.getExtension("OES_vertex_array_object");

  if (ext == null) {
    return ext;
  }

  const extWrapper = {
    bindVertexArray: function(vao) {
      ext.bindVertexArrayOES(vao);
    },
    createVertexArray: function() {
      return ext.createVertexArrayOES();
    },
    deleteVertexArray: function(vao) {
      ext.deleteVertexArrayOES(vao);
    },
    isVertexArray: function(vao) {
      return ext.isVertexArrayOES(vao);
    }
  };

  return extWrapper;
}

function getMultipleRenderTargets(gl) {

  const ext = gl.getExtension("WEBGL_draw_buffers");

  if (ext == null) {
    return ext;
  }

  const extWrapper = {
    drawBuffers: function(targets) {
      ext.drawBuffersWEBGL(targets);
    },
    COLOR_ATTACHMENT0      : ext.COLOR_ATTACHMENT0_WEBGL,
    COLOR_ATTACHMENT1      : ext.COLOR_ATTACHMENT1_WEBGL,
    COLOR_ATTACHMENT2      : ext.COLOR_ATTACHMENT2_WEBGL,
    COLOR_ATTACHMENT3      : ext.COLOR_ATTACHMENT3_WEBGL,
    MAX_DRAW_BUFFERS_WEBGL : ext.MAX_DRAW_BUFFERS_WEBGL,
  };

  return extWrapper;

}

function getInstancedArrays(gl) {

  const ext = gl.getExtension("ANGLE_instanced_arrays");

  if (ext == null) {
    return ext;
  }

  const extWrapper = {
    drawElementsInstanced: function(mode, count, type, offset, primCount) {
      ext.drawElementsInstancedANGLE(mode, count, type, offset, primCount);
    },
    vertexAttribDivisor: function(loc, div) {
      ext.vertexAttribDivisorANGLE(loc, div);
    },
    drawArraysInstanced: function(mode, first, count, primCount) {
      ext.drawArraysInstancedANGLE(mode, first, count, primCount);
    }
  };

  return extWrapper;

}

function getTextureFilterAnisotropic(gl) {
  return gl.getExtension("EXT_texture_filter_anisotropic") ||
         gl.getExtension("MOZ_EXT_texture_filter_anisotropic") ||
         gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
}

function getMinMaxBlending(gl) {

  const ext = gl.getExtension("EXT_blend_minmax");

  if (ext == null) {
    return ext;
  }

  const extWrapper = {
    MIN : ext.MIN_EXT,
    MAX : ext.MAX_EXT
  };

  return extWrapper;

}

export default {

  get(name) {

    if (name in _cache) {
      return _cache[name];
    }

    let ext = null;

    if (GL.checkWebGL2()) {
      ext = webgl2Get(name);
    } else {
      ext = webgl1Get(name);
    }

    _cache[name] = ext;

    return ext;

  },

  checkDepthWrite() {
    return GL.checkWebGL2() || Boolean(_cache["EXT_frag_depth"]) || this.get("EXT_frag_depth") !== null;
  },

  checkDepthRecalc() {
    return GL.checkWebGL2() || Boolean(_cache["WEBGL_depth_texture"]) ||
        this.checkDepthWrite() && this.get("WEBGL_depth_texture") != null;
  },

  maxSamplesCount() {
    if (_maxSamples === undefined) {
      const gl = GL.getContext();
      const samplesReturned = GL.checkWebGL2() ? gl.getParameter(gl.MAX_SAMPLES) : 1;
      _maxSamples = samplesReturned ? samplesReturned : 1;
    }
    return _maxSamples;
  },

  isMultisamplingSupported() {
    if (_multisamplingSupported === undefined) {
      const gl = GL.getContext();
      const samplesCount = this.maxSamplesCount();

      if (GL.checkWebGL2() && samplesCount > 1) {
        const fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);

        const mSamplingTexture = resourceManager.initBuffer(pixelBufferType.RB_RGBA, {
          width: 1,
          height: 1,
          format: RBIntFormat.R8G8B8A8,
          samples: samplesCount
        });
        gl.bindRenderbuffer(gl.RENDERBUFFER, mSamplingTexture.pixelBuffer);
        gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samplesCount, glTypes.getRBInternalFormat(RBIntFormat.R8G8B8A8), 1, 1);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, mSamplingTexture.pixelBuffer);

        const mSamplingDepthBuffer = resourceManager.initBuffer(pixelBufferType.RB_DEPTH,
          {width: 1, height: 1, format: RBIntFormat.D16U, samples: samplesCount});
        gl.bindRenderbuffer(gl.RENDERBUFFER, mSamplingDepthBuffer.pixelBuffer);
        gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samplesCount, glTypes.getRBInternalFormat(RBIntFormat.D16U), 1, 1);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, mSamplingDepthBuffer.pixelBuffer);

        const framebufferStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        mSamplingTexture.cleanup();
        mSamplingDepthBuffer.cleanup();
        gl.deleteFramebuffer(fbo);

        _multisamplingSupported = framebufferStatus === gl.FRAMEBUFFER_COMPLETE;
      } else {
        _multisamplingSupported = false;
      }
    }

    return _multisamplingSupported;
  },

  cleanup() {

    _cache = {};
    _webgl1Dictionary = {};
    _webgl2Dictionary = {};

  }

}
